import type { ReactNode } from 'react';
import {
  MantineThemeProvider,
  type MantineThemeProviderProps,
} from '@cowbell/ui';
import { READING_MEASURE } from '../../theme/measure';
import { CONSOLE_FONT_STACK } from '../../theme/typeface';
import { useAdapters } from '../adapters/AdapterContext';
import { BrokerGround } from './BrokerGround';

/**
 * What the theme adds to the UI kit's: component defaults only. Anything the theme turns into a CSS variable is
 * written to `:root`, where every other Mantine surface in the console reads it.
 */
/**
 * Overlays are drawn at the end of the page, outside the ground, so the face the ground sets does not reach them: a
 * menu of options, a calendar, a tooltip or a dialog. They take the console's face from here.
 */
const OVERLAY_FACE = { fontFamily: CONSOLE_FONT_STACK };

export const BROKER_THEME: MantineThemeProviderProps['themeOverrides'] = {
  components: {
    Combobox: { styles: { dropdown: OVERLAY_FACE } },
    Popover: { styles: { dropdown: OVERLAY_FACE } },
    Menu: { styles: { dropdown: OVERLAY_FACE } },
    Tooltip: { styles: { tooltip: OVERLAY_FACE } },
    Modal: { styles: { content: OVERLAY_FACE } },
    Drawer: { styles: { content: OVERLAY_FACE } },
    // Clicking the chosen option again would otherwise take the answer back: the form's field would hold null.
    Select: { defaultProps: { allowDeselect: false } },
    // A notice spans the step's width with the fields it is about, and its words keep to the reading measure.
    Alert: { styles: { message: { maxWidth: READING_MEASURE } } },
    // A field's helper line is read as the field is filled in, so it is one size up from Mantine's smallest.
    InputWrapper: {
      styles: { description: { fontSize: 'var(--mantine-font-size-sm)' } },
    },
  },
};

/**
 * The face the dashboard sets for every Mantine component, Titles included. Mantine writes a theme's font to `:root`, so
 * it reaches the whole page while the dashboard is mounted, and no longer once it is not.
 */
export const DASHBOARD_THEME: MantineThemeProviderProps['themeOverrides'] = {
  ...BROKER_THEME,
  fontFamily: CONSOLE_FONT_STACK,
  headings: { fontFamily: CONSOLE_FONT_STACK },
};

interface BrokerThemeProviderProps {
  /** Whether a host layout sizes the experience. See `BrokerRoot`. */
  readonly embedded?: boolean;
  /**
   * Whether the experience is the dashboard, which sets the console's typeface on the page. The quote experience does
   * not write it to the page; its ground gives the console's face to what is inside it (`BrokerGround`).
   */
  readonly dashboard?: boolean;
  readonly children: ReactNode;
}

/**
 * The design system, in the colour scheme the host reports through the theme port, and the ground the experience
 * renders on.
 *
 * The console mounts a Mantine provider at its root, but that one's colour scheme stays light: the real scheme lives
 * in Redux, where Mantine's own state never sees it. Forcing the scheme the host reports makes what Mantine
 * components read follow the console's, as the `data-mantine-color-scheme` attribute the design tokens switch on
 * does. Required, not optional.
 */
export function BrokerThemeProvider({
  embedded = false,
  dashboard = false,
  children,
}: BrokerThemeProviderProps) {
  const { theme } = useAdapters();

  return (
    <MantineThemeProvider
      forceColorScheme={theme.colorScheme}
      themeOverrides={dashboard ? DASHBOARD_THEME : BROKER_THEME}
    >
      <BrokerGround embedded={embedded}>{children}</BrokerGround>
    </MantineThemeProvider>
  );
}
